const state = {
    context: null,
    master: null,
    musicGain: null,
    musicTimer: null,
    nextNoteTime: 0,
    step: 0,
    musicPlaying: false,
};

const NOTE = Object.freeze({
    C4: 261.63,
    D4: 293.66,
    E4: 329.63,
    F4: 349.23,
    G4: 392.0,
    A4: 440.0,
    B4: 493.88,
    C5: 523.25,
    D5: 587.33,
    E5: 659.25,
    G5: 783.99,
});

// A short, gentle 8-bar loop: a clear melody with soft sustained chord tones.
const MELODY = [
    NOTE.G4, null, NOTE.A4, NOTE.G4, NOTE.E4, null, NOTE.G4, null,
    NOTE.A4, null, NOTE.B4, NOTE.C5, NOTE.B4, null, NOTE.A4, null,
    NOTE.G4, null, NOTE.E4, NOTE.G4, NOTE.A4, null, NOTE.G4, null,
    NOTE.E4, null, NOTE.D4, NOTE.E4, NOTE.G4, null, NOTE.C5, null,
];
const CHORDS = [
    [NOTE.C4, NOTE.E4, NOTE.G4],
    [NOTE.F4, NOTE.A4, NOTE.C5],
    [NOTE.G4, NOTE.B4, NOTE.D5],
    [NOTE.C4, NOTE.E4, NOTE.G4],
];
const STEP_SECONDS = 60 / 96 / 2;

function ensureContext() {
    if (state.context) return state.context;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;
    state.context = new AudioContextClass();
    state.master = state.context.createGain();
    state.master.gain.value = 0.18;
    state.master.connect(state.context.destination);
    state.musicGain = state.context.createGain();
    state.musicGain.gain.value = 0.42;
    state.musicGain.connect(state.master);
    return state.context;
}

function resume() {
    const context = ensureContext();
    if (context?.state === 'suspended') void context.resume();
    return context;
}

function playTone(frequency, duration, type = 'sine', volume = 0.12, when, destination = state.master) {
    const context = resume();
    if (!context || !state.master) return;
    const start = when ?? context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(Math.max(volume, 0.001), start + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain);
    gain.connect(destination || state.master);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
}

function scheduleMusic() {
    const context = state.context;
    if (!context || !state.musicPlaying || !state.musicGain) return;
    while (state.nextNoteTime < context.currentTime + 0.35) {
        const step = state.step % MELODY.length;
        const melodyNote = MELODY[step];
        if (melodyNote) playTone(melodyNote, STEP_SECONDS * 0.8, 'triangle', 0.08, state.nextNoteTime, state.musicGain);
        if (step % 8 === 0) {
            const chord = CHORDS[Math.floor(step / 8) % CHORDS.length];
            chord.forEach((frequency, index) => {
                playTone(frequency, STEP_SECONDS * 7.5, 'sine', 0.018 - index * 0.003, state.nextNoteTime, state.musicGain);
            });
        }
        state.nextNoteTime += STEP_SECONDS;
        state.step += 1;
    }
}

function startMusic() {
    const context = resume();
    if (!context || state.musicPlaying) return state.musicPlaying;
    state.musicPlaying = true;
    state.nextNoteTime = context.currentTime + 0.05;
    state.step = 0;
    scheduleMusic();
    state.musicTimer = window.setInterval(scheduleMusic, 120);
    return true;
}

function stopMusic() {
    state.musicPlaying = false;
    if (state.musicTimer) window.clearInterval(state.musicTimer);
    state.musicTimer = null;
}

function toggleMusic() {
    if (state.musicPlaying) {
        stopMusic();
        return false;
    }
    return startMusic();
}

const sound = Object.freeze({
    resume,
    startMusic,
    stopMusic,
    toggleMusic,
    tap: () => playTone(520, 0.07, 'sine', 0.06),
    correct: () => {
        const context = resume();
        if (!context) return;
        playTone(NOTE.C5, 0.13, 'triangle', 0.1, context.currentTime);
        playTone(NOTE.G5, 0.2, 'triangle', 0.1, context.currentTime + 0.1);
    },
    wrong: () => playTone(180, 0.18, 'sawtooth', 0.08),
    castleHit: () => {
        const context = resume();
        if (!context) return;
        playTone(130, 0.18, 'square', 0.08, context.currentTime);
        playTone(90, 0.22, 'triangle', 0.06, context.currentTime + 0.08);
    },
    win: () => {
        const context = resume();
        if (!context) return;
        [NOTE.C5, NOTE.E5, NOTE.G5].forEach((frequency, index) => playTone(frequency, 0.25, 'triangle', 0.1, context.currentTime + index * 0.12));
    },
    gameOver: () => {
        const context = resume();
        if (!context) return;
        [NOTE.G4, NOTE.E4, NOTE.C4].forEach((frequency, index) => playTone(frequency, 0.28, 'sine', 0.08, context.currentTime + index * 0.14));
    },
});

export default sound;
